import {inGamecenter, qsStringify, isVkPlay, isMyGames} from '@vkplay/shared';
import Loader from '@components/Loader';
import {changeCookieLanguage} from '@utils/services';
import VueEasyLightbox from 'vue-easy-lightbox';
import DocumentPreview from '../DocumentPreview';
import Currency from '../Currency';
import Tax from '../Tax';
import Vue from 'vue';
import ContentTable from "../ContentTable";

export default {
  name: 'ContentBlock',
  components: {
    Loader,
    DocumentPreview,
    VueEasyLightbox,
    Currency,
    Tax,
  },
  props: {
    link: {
      type: String,
      default: '',
    },
    defaultLink: {
      type: String,
      default: '',
    },
  },
  computed: {
    document() {
      return this.$store.getters['docs/getterDocument'];
    },
    documentIndex() {
      return this.documentsList
        .findIndex(item => item?.slug === this.document.data.slug);
    },
    contents() {
      return this.$store.getters['docs/getterContents'];
    },
    documentsList() {
      return this.contents?.data?.results
        ?.map(item => item.documents)
        .flat(2);
    },
    prevDocument() {
      if (this.documentIndex > 0) {
        return this.documentsList[this.documentIndex - 1];
      }

      return undefined;
    },
    nextDocument() {
      if (this.documentIndex <= this.documentsList.length) {
        return this.documentsList[this.documentIndex + 1];
      }

      return undefined;
    },
    termsLangList() {
      if (this.document.data.languages) {
        const langNames = this.document.data.languages.map(lang => {
          const stringifiedParams = qsStringify({
            inGamecenter: inGamecenter ? 1 : undefined,
            tag: this.document.data.version ? this.documentVersion : undefined,
            switch: this.$route.query.switch === '1' ? 1 : undefined,
            lang: lang.locale
          }, {
            addQueryPrefix: true
          });

          return {
            locale: lang.locale,
            name: lang.name,
            url: `${window.location.pathname}${stringifiedParams}`
          }
        });

        return langNames;
      }

      return [];
    },
    isDocumentNotFound() {
      return !!this.document.error;
    },
    isLanguageError() {
      const availableDocumentLang = (this.document.data.languages ?? [])
        .find(item => item === 'ru_RU');

      return !!availableDocumentLang && this.$i18n.locale === 'ru_RU';
    },
    flagLang() {
      const langInstance = this.$route.query?.lang || this.$i18n.locale;

      return langInstance.split('_')?.[0];
    },
    documentText() {
      let {text} = this.document.data;
      const hrefPatternStart = /<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1/ig;
      const hrefPatternEnd = /((<\/)a(>))/ig;
      const linkParam = this.$route.query.switch;

      if (!text) {
        return '';
      }

      // Заменить ссылки в доке на span, при передаче параметра в url
      text = linkParam === '1' ? text
        .replace(hrefPatternStart, '<span style="color: #6a6a7b"')
        .replace(hrefPatternEnd, '</span>') : text;

      // Добавить параметр in_gamecenter=1 в ссылку с предыдущей версией документа
      if (this.isTerms && this.inGamecenter) {
        text = text.replace('/terms/mygames_eula/?tag=', '/terms/mygames_eula/?in_gamecenter=1&tag=');
      }

      return text;
    },
    documentVersion() {
      if (!this.document.data.version) {
        return '';
      }
      return this.document.data.version;
    },
    getDocumentLangs() {
      return this.document.data.languages;
    },
    isTermsDocs() {
      return this.$route.path.indexOf('/terms/') > -1 || this.$route.path.indexOf('/terms_vkplay/') > -1
    },
    isCurrencyPage() {
      return this.$route.path.indexOf('/exchange_rates') > -1;
    },
    isTaxPage() {
      return this.$route.path.indexOf('/tax_rates') > -1;
    },
    isAvailableLangsVisible() {
      return this.getDocumentLangs
        && this.getDocumentLangs.length >= 1
        && !this.inGamecenter;
    },
    inGamecenter() {
      return !!inGamecenter;
    },
    isMGVCPage() {
      return document.domain.endsWith('mgvc.com');
    },
    isNoMenu() {
      return this.$route.query.nomenu === '1';
    },
    isContentUnavailable() {
      return this.$i18n.locale !== 'en_US' && this.$i18n.locale !== 'ru_RU';
    },
  },
  data() {
    return {
      isOpenLangDropdown: false,
      cookieBotLoaded: false,
      isLightboxVisible: false,
      lightboxImage: '',
    };
  },
  created() {
    this.fetchDocument();
  },
  mounted() {
    this.startMutationObserver();

    if (this.$route.hash) {
      const hash = this.$route.hash.substring(1);
      let scrolled = this.navigateTo(hash);

      if (!scrolled) {
        const int = setInterval(() => {
          scrolled = this.navigateTo(hash);

          if (scrolled) {
            clearInterval(int);
          }
        }, 500);
      }

      window.onload = () => {
        if (window.history.scrollRestoration) {
          window.history.scrollRestoration = "manual";
        }
      }
    }

    window.addEventListener('click', this.closeDropdown);
  },
  beforeDestroy() {
    window.removeEventListener('click', this.closeDropdown);
  },
  watch: {
    '$route.params.documentSlug': {
      handler: 'fetchDocument',
    },
    '$route.query.lang': {
      handler: 'fetchDocument',
    },
  },
  methods: {
    fetchDocument() {
      this.$store.dispatch('docs/fetchDocument', {
        projectSlug: this.$route.params.projectSlug,
        documentSlug: this.$route.params.documentSlug,
        query: {
          lang: this.$route.query.lang,
          tag: this.$route.query.tag,
          ts: this.$route.query.ts,
        },
      }).then(() => {
        if (isMyGames) {
          this.initContentTable();
        }
      });
    },
    //
    closeDropdown(e) {
      if (this.openDropdown && !this.$refs.lang.contains(e.target)) {
        this.openDropdown = false;
      }
    },
    openContentImages(e) {
      if (e.target.tagName === 'IMG') {
        this.isLightboxVisible = true;
        this.lightboxImage = e.target.src;
      }
    },
    toggleDropdown() {
      this.isOpenLangDropdown = !this.isOpenLangDropdown;
    },
    navigateTo(hash) {
      const el = document.getElementById(hash);

      if (el) {
        el.scrollIntoView();

        return true;
      }
      return false;
    },
    setLang(lang) {
      if (lang) {
        changeCookieLanguage(lang);
      }
    },
    startMutationObserver() {
      const observer = new MutationObserver(this.handleObserverMutations);
      const config = {childList: true, subtree: true};

      observer.observe(document.body, config);

      return observer;
    },
    handleObserverMutations(...args) {
      this.tryToLoadCookiebotTableDeclaration(...args);
    },
    initContentTable() {
      const search = new URLSearchParams(location.search);
      const searchLang = search.get('lang');
      const locale = (searchLang && searchLang.split('_')[0]) || this.$i18n.locale.split('_')[0];
      const tableContainer = document.getElementById('__cookie_table');

      if (!tableContainer) {
        return;
      }

      const child = document.createElement('div');
      child.id = "cookie_table_custom";
      tableContainer.querySelector("table").innerHTML = "";
      tableContainer.appendChild(child);

      const tableContainerCustom = document.getElementById('__cookie_table_custom');

      const messages = {
        name: this.$i18n.t('contentTable.name'),
        expire: this.$i18n.t('contentTable.expire'),
        type: this.$i18n.t('contentTable.type'),
        description: this.$i18n.t('contentTable.description'),
        vendor: this.$i18n.t('contentTable.vendor'),
        collapse: this.$i18n.t('contentTable.collapse'),
        expand: this.$i18n.t('contentTable.expand'),
      }

      if (!tableContainerCustom) {
        new Vue({
          props: ['lang'],
          render: h => h(ContentTable, { props: { lang: locale, messages}})
        }).$mount('#cookie_table_custom');
      }
      const oldTable = tableContainer.getElementsByClassName('Table');
      if (oldTable.length) {
        tableContainer.removeChild(oldTable[0]);
      }
    },
    tryToLoadCookiebotTableDeclaration() {
      const tableContainer = document.getElementById('__cookie_table');
      const search = new URLSearchParams(location.search);
      const searchLang = search.get('lang');
      const locale = (searchLang && searchLang.split('_')[0]) || this.$i18n.locale.split('_')[0];
      const cookieConsent = search.get('cookieconsent');

      if (!tableContainer) {
        this.cookieBotLoaded = false;

        return;
      }

      // Если есть контейнер и не загружен кукибот, то добавляем скрипт
      if (tableContainer && !this.cookieBotLoaded) {
        this.cookieBotLoaded = true;
        const cookieScript = document.createElement('script');

        cookieScript.async = false;

        if (isMyGames) {
          return;
          // // Ссылка на таблицу с описанием кук
          //
          // cookieScript.setAttribute('data-culture', locale)
          // cookieScript.src = 'https://consent.cookiebot.com/64bfd89d-23e9-426d-a748-3fc048a338b1/cd.js'
          // cookieScript.onload = () => {
          //   const cleaning = (rows, regExp) => {
          //     rows.forEach((row) => {
          //       const contains = regExp.test(row.innerText)
          //       const cell = row.querySelectorAll('td')[1]
          //       const cellLength = cell && cell.childNodes.length
          //
          //       if (contains) {
          //         if (cellLength && cellLength <= 1) {
          //           row.innerHTML = ''
          //         }
          //         if (cellLength && cellLength > 1) {
          //           row.innerHTML = row.innerHTML.replace(regExp, '')
          //         }
          //       }
          //     })
          //   }
          //
          //   const work = () => {
          //     const tables = document.querySelectorAll('.CookieDeclarationTable')
          //     const rows = document.querySelectorAll('.CookieDeclarationTable tr')
          //     const regExp = /(yandex|mail\.ru|media scope|vk)/ig
          //     cleaning(rows, regExp)
          //     cleaning(rows, regExp) // одного прохода мало, лагает из-за количества ячеек
          //
          //     tables.forEach((table) => {
          //       table.style.display = 'table'
          //     })
          //     console.info('[cookiebot_table_loaded]')
          //   }
          //
          //   const interval = setInterval(() => {
          //     const elems = document.querySelectorAll('.CookieDeclarationTable tr')
          //     if (elems.length) {
          //       clearInterval(interval)
          //       work()
          //     }
          //   }, 100)
          // }
        }

        if (isVkPlay || cookieConsent) {
          const container = document.createElement('div');
          const headerLine = tableContainer.getElementsByTagName('h2');

          container.id = 'cmpcookieinfo';
          headerLine.length ? tableContainer.insertBefore(container, headerLine[0]) : tableContainer.appendChild(container);

          cookieScript.src = `https://delivery.consentmanager.net/delivery/cookieinfo.php?cdid=5475788091d81${locale ? '&l=' + locale : ''}`;
          cookieScript.onload = () => {
            console.log('ConsentManager cookie loaded')
          }
        }

        tableContainer.appendChild(cookieScript);

        // Если есть дефолтная таблица в доке, то удаляем
        const oldTable = tableContainer.getElementsByClassName('Table');
        if (oldTable.length) {
          tableContainer.removeChild(oldTable[0]);
        }
      }
    },
    onLightboxHide() {
      this.isLightboxVisible = false;
    },
  },
};
